import React, { useEffect } from 'react';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { AppShell, Spinner } from '@billda/design-system';
import { QueryClientProvider } from '@tanstack/react-query';
import AppErrorBoundary from '../shared/components/ui/AppErrorBoundary';
import AppRouter from '../shared/components/layout/AppRouter';
import { useAuthStore } from '../features/auth/stores/authStore';
import { useResetErrorStateOnLogout } from '../shared/hooks/useResetErrorStateOnLogout';
import { ConstructionThemeProvider } from '../shared/components/theme/ThemeProvider';
import { queryClient } from '../shared/lib/queryClient';
import { PermissionsProviderAdapter } from './providers/PermissionsProviderAdapter';
import { FeaturesProviderAdapter } from './providers/FeaturesProviderAdapter';
import { DebugPanel } from '../shared/components/dev/DebugPanel';
import { FeatureSwitchGate } from '../shared/components/gates/FeatureSwitchGate';
import { Features } from '@billda/features-ui';
import { PermissionGate } from '../shared/components/gates/PermissionGate';
import { Permissions } from '@billda/permissions-ui';
import { CommandPaletteProvider } from '../features/search/providers/CommandPaletteProvider';
import { TopAppBar } from '../shared/components/layout/TopAppBar';
import { ConnectionErrorDialog } from '../shared/components/errors/ConnectionErrorDialog';
import { OrgChangedDialog } from '../shared/components/errors/OrgChangedDialog';
import { AppSnackbar } from '../shared/components/ui/AppSnackbar';
import { UploadTray } from '../features/sites/components/files/UploadTray';
import { GettingStartedFab } from '../features/onboarding/components/GettingStartedFab';
import { CompanyDetailsBanner } from '../features/organisation/components/CompanyDetailsBanner';
import { useAuthInitialization } from '../features/auth/hooks/useAuthInitialization';
import { useOrganizationSync } from '../features/organisation/hooks/useOrganizationSync';
import { ExpandedSidebar } from '../shared/components/layout/sidebar/ExpandedSidebar';
import { useSwipeToOpenSidebar } from '../shared/hooks/useSwipeToOpenSidebar';

const AppContentInner: React.FC = () => {
  const isAuthReady = useAuthInitialization();
  useResetErrorStateOnLogout();
  useOrganizationSync();

  const { isAuthenticated } = useAuthStore();
  const location = useLocation();
  const swipeRef = useSwipeToOpenSidebar();

  // Check if we're in the invitation flow or download app page (should hide navigation even when authenticated)
  const isInvitationFlow = location.pathname.startsWith('/invitation/');
  const isDownloadAppPage = location.pathname === '/download-app';
  const shouldShowNavigation =
    isAuthenticated && !isInvitationFlow && !isDownloadAppPage;

  // Handle unhandled promise rejections - must be called before any early returns
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection:', event.reason);
      // Prevent the default behavior (console error)
      event.preventDefault();
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection
      );
    };
  }, []);

  // Show loading screen while checking authentication
  if (!isAuthReady) {
    return (
      <div className="flex h-dvh w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <AppShell
      ref={swipeRef}
      sideNav={shouldShowNavigation ? <ExpandedSidebar /> : undefined}
      topBar={
        shouldShowNavigation ? (
          <>
            <TopAppBar />
            {/* Global company details banner — above the scrolling content */}
            <CompanyDetailsBanner />
          </>
        ) : undefined
      }
    >
      <AppRouter />
    </AppShell>
  );
};

const AppContent: React.FC = () => {
  return (
    <AppErrorBoundary>
      <ConstructionThemeProvider>
        <PermissionsProviderAdapter>
          <FeaturesProviderAdapter>
            <BrowserRouter>
              <CommandPaletteProvider>
                <AppContentInner />
              </CommandPaletteProvider>
              <GettingStartedFab />
              <ConnectionErrorDialog />
              <OrgChangedDialog />
              <AppSnackbar />
              <FeatureSwitchGate feature={Features.Files.IsEnabled}>
                <UploadTray />
              </FeatureSwitchGate>
            </BrowserRouter>
            <PermissionGate
              permission={Permissions.Developer.Access}
              showRequiredPermissions={false}
            >
              <FeatureSwitchGate feature={Features.Debug.IsPanelEnabled}>
                <DebugPanel />
              </FeatureSwitchGate>
            </PermissionGate>
          </FeaturesProviderAdapter>
        </PermissionsProviderAdapter>
      </ConstructionThemeProvider>
    </AppErrorBoundary>
  );
};

const App: React.FC = () => {
  // Remove HTML loading screen when React is ready
  useEffect(() => {
    const loadingScreen = document.getElementById('app-loading-screen');
    if (loadingScreen) {
      // Add fade-out class for smooth transition
      loadingScreen.classList.add('fade-out');
      // Remove from DOM after animation completes
      setTimeout(() => loadingScreen.remove(), 300);
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AppContent />
    </QueryClientProvider>
  );
};

export default App;
